import { BankIdApiService } from "@api-services/integrations/BankIdApiService";
import { BankIdStepUpAction } from "@data-types/user/BankIdStepUpAction";
import { SessionStorageProvider } from "@state/providers";

import {
	BANKID_QUERY_PARAMS,
	BankIdStorageKey,
} from "@utils/specific/bankid/bankIdConstants";
import {
	isBankIdRedirectCancel,
	bankIdRedirectMessageKey,
} from "@utils/specific/bankid/bankIdRedirect";
import { getRedirectOriginUrl } from "@utils/specific/capacitor/helpers";

const STEP_UP_STORAGE_KEY = BankIdStorageKey.StepUpSnapshot;

type StepUpSnapshot = {
	action: BankIdStepUpAction;
	payload: unknown;
};

export type StepUpReturn =
	| { status: "success"; sessionId: string; payload: any }
	| { status: "failed"; messageKey: string }
	| { status: "none" };

export function isBankIdPasswordlessUser(
	user?: { isRegisteredWithExternalProvider?: boolean } | null
): boolean {
	return Boolean(
		WEBPACK_IS_BANKID_ENABLED && user?.isRegisteredWithExternalProvider
	);
}

export async function startWebStepUp(
	action: BankIdStepUpAction,
	payload: unknown
): Promise<void> {
	SessionStorageProvider.set(
		STEP_UP_STORAGE_KEY,
		JSON.stringify({ action, payload } as StepUpSnapshot)
	);

	const successRedirectUrl = getRedirectOriginUrl(
		window.location.pathname,
		true
	);
	const failureRedirectUrl = getRedirectOriginUrl(
		window.location.pathname + "?failedAuth=true",
		true
	);

	const response = await BankIdApiService.getBankIdWidgetInfo(
		"same-device",
		successRedirectUrl,
		failureRedirectUrl
	);

	if (response?.redirectUrl) {
		window.location.replace(response.redirectUrl);
		return;
	}

	SessionStorageProvider.remove(STEP_UP_STORAGE_KEY);
	throw response;
}

export function consumeWebStepUp(action: BankIdStepUpAction): StepUpReturn {
	const params = new URLSearchParams(window.location.search);

	const cancel = isBankIdRedirectCancel();

	if (params.get("failedAuth") || params.get("errorDetails")) {
		clearWebStepUp();
		return {
			status: "failed",
			messageKey: bankIdRedirectMessageKey(cancel),
		};
	}

	const raw = SessionStorageProvider.get(STEP_UP_STORAGE_KEY);
	const snapshot: StepUpSnapshot | null = raw ? JSON.parse(raw) : null;

	if (!snapshot || snapshot.action !== action) {
		return { status: "none" };
	}

	const sessionId = params.get("sessionId");

	if (sessionId) {
		clearWebStepUp();
		return { status: "success", sessionId, payload: snapshot.payload };
	}

	if (params.get("sessionStatus")) {
		clearWebStepUp();
		return {
			status: "failed",
			messageKey: bankIdRedirectMessageKey(cancel),
		};
	}

	return { status: "none" };
}

export function clearWebStepUp(): void {
	SessionStorageProvider.remove(STEP_UP_STORAGE_KEY);

	const params = new URLSearchParams(window.location.search);
	BANKID_QUERY_PARAMS.forEach((param) => params.delete(param));

	const query = params.toString();
	window.history.replaceState(
		{},
		"",
		window.location.pathname + (query ? `?${query}` : "")
	);
}
